/**
 * WHERE an approved action will run, named for the human.
 *
 * The model is told a saved connection's `conn_id` and NAME and nothing else
 * (owner rule, 17.09.2026: from the Vault an agent sees only the name of an
 * entry). That is exactly why the approval card has to say more: the one who has
 * to recognise the target is the user, and a bare id names nothing. Before that
 * rule the model's own prose usually carried the hostname, which hid the fact
 * that the card never did — and `db_run_sql` showed "Run SQL:" with no target
 * at all.
 *
 * Everything here is local UI. Nothing it returns is sent to the model.
 *
 * Both formatters are pure and return `null` for an id they cannot resolve
 * (store not loaded, Vault locked, an id the model made up); the card then shows
 * the id verbatim rather than guessing.
 */
import { useEffect, useState } from 'react';
import { useFtpStore } from '../../store/ftpStore';
import { listDbCapableExtensionIds, listSavedDbConnections } from '../../extensions/capabilities/db';

/** `name (username@host:port)` — the shape McpTerminalApprovalModal shows on the MCP path. */
export function terminalTarget(connections, connId) {
  const c = (Array.isArray(connections) ? connections : []).find((x) => x?.id === connId);
  if (!c) return null;
  const name = typeof c.name === 'string' && c.name ? c.name : String(connId);
  const host = c.index?.host ? String(c.index.host) : '';
  if (!host) return name;
  const user = c.index?.username ? `${c.index.username}@` : '';
  const port = c.index?.port ? `:${c.index.port}` : '';
  return `${name} (${user}${host}${port})`;
}

/**
 * `name (engine · user@host:port/database)`, or `name (sqlite · <path>)`.
 * `database` is the one the SQL will actually run against: the tool's own
 * `database` argument when given, else the saved connection's default.
 */
export function dbTarget(descriptor, input) {
  if (!descriptor) return null;
  const name = descriptor.name || String(descriptor.id ?? '');
  const engine = descriptor.engine || 'database';
  if (descriptor.path) return `${name} (${engine} · ${descriptor.path})`;
  const database = (input && typeof input.database === 'string' && input.database) || descriptor.database || '';
  if (!descriptor.host) return database ? `${name} (${engine} · ${database})` : `${name} (${engine})`;
  const user = descriptor.user ? `${descriptor.user}@` : '';
  const port = descriptor.port ? `:${descriptor.port}` : '';
  return `${name} (${engine} · ${user}${descriptor.host}${port}${database ? `/${database}` : ''})`;
}

async function findSavedDbConnection(connId) {
  for (const extensionId of listDbCapableExtensionIds()) {
    const saved = await listSavedDbConnections(extensionId).catch(() => []);
    const hit = saved.find((c) => c?.id === connId);
    if (hit) return hit;
  }
  return null;
}

/**
 * The target label for the tool on this card, or null when there is none to
 * show. Terminal targets come from the FTP store (the SSH connections are its
 * sftp entries); DB targets are read from the Vault index, which is async.
 */
export function useApprovalTarget(toolName, toolInput) {
  const connId = toolInput?.conn_id;
  const isTerminal = toolName === 'terminal_run_command';
  const isDb = toolName === 'db_run_sql';

  const terminal = useFtpStore((s) => (isTerminal ? terminalTarget(s.connections, connId) : null));
  const connectionsLoaded = useFtpStore((s) => s.connectionsLoaded);
  useEffect(() => {
    // The model may reuse a conn_id from earlier in the conversation without
    // listing again (after a reload the store starts empty), so fetch the list
    // here — otherwise the card could only show the bare id.
    if (isTerminal && !connectionsLoaded) useFtpStore.getState().loadConnections().catch(() => {});
  }, [isTerminal, connectionsLoaded]);

  // The answer is stored WITH the id it answers for, and only used while that is
  // still the id on the card. A bare descriptor would survive a change of
  // `conn_id` until the next lookup resolved — and for that moment the card
  // would name the PREVIOUS database over a statement bound for another one.
  // An approval card may be wrong by omission (the bare id); it must never be
  // wrong by naming a different target.
  const [dbAnswer, setDbAnswer] = useState({ connId: null, descriptor: null });
  useEffect(() => {
    if (!isDb || !connId) return undefined;
    let cancelled = false;
    findSavedDbConnection(connId)
      .then((hit) => { if (!cancelled) setDbAnswer({ connId, descriptor: hit }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isDb, connId]);

  if (isTerminal) return terminal;
  if (isDb) return dbTarget(dbAnswer.connId === connId ? dbAnswer.descriptor : null, toolInput);
  return null;
}
